import _                                                                     from 'underscore';
import classNames                                                            from 'classnames';
import React, { FC, ReactNode, useContext }                                  from 'react';
import { Dropdown, DropdownMenuProps, DropdownProps, Icon, Menu, MenuProps } from 'semantic-ui-react';
import { useHistory }                                                        from 'react-router';

import { CorvalProject, FlexAlign, Size, Uuid }                                     from '../../../../types';
import { RouterUtility, runIfFunction }                                             from '../../../../util';
import { useCurrentUser, useFeatureFlags, useProject, usePrompts, useSystemModals } from '../../../../hooks';
import { Flexbox, FlexItem, StyledContainer }                                       from '../../../containers';
import { ProjectContext }                                                           from '../ProjectIndexPage';
import { PROJECT_ROLES }                                                            from '../../../../constants/ProductEnums';
import { EXPORTABLE_FILE_TYPES, ExportProjectDialog }                               from './ExportProjectDialog';
import { UseIf }                                                                    from "@src/views/components/display/EqualValues";
import { FinePrintMessage }                                                         from "@src/views/components/util/messaging/FinePrintMessage";
import { useLocation }                                                              from 'react-router-dom';
import { ItemWithIcon } from '@src/views/components/display/ItemWithIcon';
import { ImportDataDialog }      from './ImportDataDialog';
import { ImportSmartsheetDialog } from './ImportSmartsheetDialog';
import { ImportCustomSmartsheetDialog } from './ImportCustomSmartsheetDialog';
import { ExportSmartsheetDialog } from './ExportSmartsheetDialog';
import { RollUpVersionsDialog }  from './RollUpVersionsDialog';
import { DownloadPdfDialog }     from './DownloadPdfDialog';
import { SurveyBuilderDialog }   from './SurveyBuilderDialog';
import { useCentralCalls }       from '@src/rest/CentralCalls';


interface Props {
    projectId?: Uuid,
    projectVersionId?: Uuid,
    className?: string,
    label?: string,
    direction?: DropdownProps['direction'],
    showExport?: boolean,
    showSupport?: boolean,
    showVersionControl?: boolean,
    showSwitchProjects?: boolean,
    showTemplateCurrentProject?: boolean,
    showTemplateManagement?: boolean,
    showImportProjectData?:  boolean,
    showDownloadPdf?:        boolean,
    showRollUpVersions?: boolean,
    showSurveyBuilder?: boolean,
    showCreateProject?: boolean,
    showLogout?: boolean,
    onRemapComplete?: ( project: CorvalProject ) => void,
    onLoadAllContentComplete?: ( project: CorvalProject ) => void,
    onUnloadAllContentComplete?: ( project: CorvalProject ) => void,
    useDropdown?: boolean,
    containerProps?: MenuProps | DropdownMenuProps
}

type MenuHash = Array<MenuHashItem>;

interface MenuHashItem {
    header: string,
    items: Array<{ onClick: () => void, content: ReactNode }>
}

const defaultProps: Props = {
    projectId:                    null,
    projectVersionId:             null,
    className:                    null,
    label:                        'Menu',
    direction:                    'right',
    showExport:                   true,
    showSupport:                  true,
    showVersionControl:           true,
    showSwitchProjects:           false,
    showTemplateCurrentProject:   false,
    showTemplateManagement:       false,
    showImportProjectData:        true,
    showDownloadPdf:              true,
    showRollUpVersions:           false,
    showSurveyBuilder:            true,
    showCreateProject:            false,
    showLogout:                   false,
    onRemapComplete:              null,
    onLoadAllContentComplete:     null,
    onUnloadAllContentComplete:   null,
    useDropdown:                  true,
    containerProps:               {},
};

const ProjectOperationsMenu: FC<Props> = ( props ) => {

    let projectContext   = useContext( ProjectContext ),
        projectId        = props.projectId ?? projectContext.projectId ?? null,
        projectVersionId = props.projectVersionId ?? projectContext.projectVersionId ?? null;

    const { downloadStaticProjectActivityTemplate, importStaticProjectActivities, executeStoredProcedure } = useCentralCalls();

    if( !projectId ) return null;

    let {
            showProjectVersionManagementDialog,
            showProjectTemplateCreationDialog,
            showProjectTemplatesManagementDialog,
            showCreateProjectFromTemplateDialog,
            createNewProjectVersion
        }                        = useProject( projectId ),
        { promptSupportActions } = usePrompts(),
        modalActions             = useSystemModals(),
        location                 = useLocation(),
        history                  = useHistory(),
        featureFlags             = useFeatureFlags();

    const { currentUser } = useCurrentUser();

    const currentProjectRole = currentUser?.projects?.find(
        ( p: { projectId: Uuid; projectRole: string } ) => p.projectId === projectId
    )?.projectRole;

    const isViewer = currentProjectRole === PROJECT_ROLES.VIEWER;
    const isAdmin  = currentProjectRole === PROJECT_ROLES.ADMIN;

    // Download PDF is only relevant for these Waypoint Biopharma projects
    const WAYPOINT_PROJECT_NAMES = [ 'One Year to Launch', 'Two Years to Launch', 'Three Years to Launch' ];
    const isWaypointProject = WAYPOINT_PROJECT_NAMES.some(
        name => name.toLowerCase() === ( projectContext.projectName ?? '' ).toLowerCase()
    );

    let { className, direction, label }                          = props,
        { onRemapComplete, useDropdown }                         = props,
        { onLoadAllContentComplete, onUnloadAllContentComplete } = props,
        { containerProps }                                       = props;

    const handleExportProject = () => {

        /* Launch data export modal */
        modalActions.show( (

            <ExportProjectDialog
                projectId={ projectId }
                initialTargetFileType={ EXPORTABLE_FILE_TYPES.PPT }
            />

        ), {
            size:     'tiny',
            centered: false,
        } );
    };

    const handleTemplateCurrentProject = () => {

        showProjectTemplateCreationDialog( projectId, projectVersionId, () => {

            modalActions.toggle( false );
        } );
    };

    const handleManageProjectTemplates = () => {

        showProjectTemplatesManagementDialog( async ( templateListing ) => {

            // modalActions.toggle( false );

        } );
    };

    const handleCreateNewProject = () => {

        showCreateProjectFromTemplateDialog( () => {} );
    };

    const handleOpenSurveyBuilder = () => {

        modalActions.show( (

            <SurveyBuilderDialog onClose={ () => modalActions.toggle( false ) }/>

        ), {
            size:                 'large',
            centered:             true,
            closeOnDocumentClick: false,
            closeOnDimmerClick:   false,
        } );
    };

    const handleCreateNewProjectVersion = () => {

        createNewProjectVersion( projectId, projectVersionId, ( newVersionId ) => {

            // Make the new version the currently active version
            if( projectContext.setProjectVersion ){
                runIfFunction( projectContext.setProjectVersion, newVersionId );
            }

        } );
    };

    const handleManageProjectVersions = () => {

        showProjectVersionManagementDialog( projectId );
    };

    let handleGotoProjectSelectionPage = () => {

        history.push( RouterUtility.getRoute( 'DASHBOARD' ) );
    };

    let handleGotoLogoutPage = () => {

        history.push( RouterUtility.getRoute( 'LOGOUT' ) );
    };

    let handleReportIssue = () => {

        promptSupportActions();

    };


    const handleDownloadImportTemplate = async () => {
        try{

            await downloadStaticProjectActivityTemplate( 'xlsx', true );
        } catch( err ) {
            modalActions.show( (
                <StyledContainer padding={ Size.SM }>
                    <ItemWithIcon iconName="exclamation triangle" iconColor="red" title="Download failed">
                        { ( err as Error ).message }
                    </ItemWithIcon>
                </StyledContainer>
            ), { size: 'tiny', centered: true } );
        }
    };


    const handleImportData = () => {
        modalActions.show(
            <ImportDataDialog
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                onComplete={ ( result ) => {
                    // Handle successful import - could refresh data, navigate, etc.
                    console.log( 'Import completed:', result );
                } }
                onClose={ () => {
                    modalActions.toggle( false );
                    window.location.reload();
                } }
            />,
            {
                size:                 'small',
                centered:             true,
                closeOnDocumentClick: false,
                closeOnDimmerClick:   false,
            }
        );
    };

    const handleImportSmartsheet = () => {
        modalActions.show(
            <ImportSmartsheetDialog
                projectId={projectId}
                projectVersionId={projectVersionId}
                onComplete={(result) => {
                    console.log('Smartsheet import completed:', result);
                    modalActions.toggle(false);
                }}
                onClose={() => modalActions.toggle(false)}
            />,
            {
                size: 'small',
                centered: true,
                closeOnDocumentClick: false,
                closeOnDimmerClick: false,
            }
        );
    };

    const handleExportSmartsheet = () => {
        modalActions.show(
            <ExportSmartsheetDialog
                projectVersionId={projectVersionId}
                onComplete={(result) => {
                    console.log('Smartsheet export completed:', result);
                    modalActions.toggle(false);
                }}
                onClose={() => modalActions.toggle(false)}
            />,
            {
                size: 'small',
                centered: true,
                closeOnDocumentClick: false,
                closeOnDimmerClick: false,
            }
        );
    };

    const handleImportCustomSmartsheet = () => {
        modalActions.show(
            <ImportCustomSmartsheetDialog
                projectId={projectId}
                projectVersionId={projectVersionId}
                onComplete={(result) => {
                    console.log('Custom format smartsheet import completed:', result);
                    modalActions.toggle(false);
                }}
                onClose={() => modalActions.toggle(false)}
            />,
            {
                size: 'small',
                centered: true,
                closeOnDocumentClick: false,
                closeOnDimmerClick: false,
            }
        );
    };
    const handleDownloadPdf = () => {

        modalActions.show( (

            <DownloadPdfDialog
                projectName={ projectContext.projectName }
                onClose={ () => modalActions.toggle( false ) }
            />

        ), {
            size:     'tiny',
            centered: true,
        } );
    };

    console.log("PROJECT OPS MENU PROJECT FERSIONID:", projectVersionId);
    const handleRollUpVersions = () => {

        modalActions.show( (
            <RollUpVersionsDialog projectVersionId={projectVersionId}
                onConfirm={ async ( versionName ) => {
                    await executeStoredProcedure( 'proc_CreateRollupObjectiveProjectVersion', {
                        projectVersionId,
                        ProjectVersionName: versionName,
                    } );
                    modalActions.toggle( false );
                } }
                onClose={ () => modalActions.toggle( false ) }
            />
        ), {
            size:                 'tiny',
            centered:             true,
            closeOnDocumentClick: false,
            closeOnDimmerClick:   false,
        } );
    };

    const showLogout                   = props.showLogout                   && !isViewer,
        showSwitchProjects           = props.showSwitchProjects           && !isViewer,
        showExport                   = props.showExport                   && !isViewer,
        showSupport                  = props.showSupport,
        showVersionControl           = props.showVersionControl && featureFlags.enableAdHocProjectVersionControlTool,
        showTemplateCurrentProject   = props.showTemplateCurrentProject && featureFlags.enableProjectTemplating,
        showCreateProject            = props.showCreateProject && featureFlags.enableProjectTemplating,
        showTemplateManagement       = props.showTemplateManagement && featureFlags.enableProjectTemplating,
        showSurveyBuilder            = props.showSurveyBuilder && !isViewer,
        showImportProjectData        = props.showImportProjectData && featureFlags.enableImportProjectData,
		showDownloadPdf              = props.showDownloadPdf && ( isViewer || isAdmin ) && isWaypointProject,
        showRollUpVersions           = props.showRollUpVersions && showVersionControl;

    const hasDataSection       = ( showExport || showTemplateCurrentProject || showCreateProject || showSurveyBuilder ),
        hasOperationsSection = ( showVersionControl || showRollUpVersions ),
        hasUserSection       = ( showLogout || showSwitchProjects );

    const menuHash: MenuHash = _.compact( [
        showDownloadPdf ? {
            header: 'Download',
            items: [

                { onClick: handleDownloadPdf, content: <><Icon name="file pdf" color="red" /> Download PDF</> },

            ]
        } : null,
        showSupport ? {
            header: 'Support',
            items:  _.compact( [

                { onClick: handleReportIssue, content: <><Icon name={ 'bug' } color={ 'red' }/> Report an Issue</> },

            ] )
        } : null,
        hasOperationsSection ? {
            header: 'Operations',
            items:  _.compact( [

                /* Add project version */
                showVersionControl
                ? { onClick: handleCreateNewProjectVersion, content: <><Icon name={ 'plus square' } color={ 'purple' }/>New Version</> } : null,

                /* Manage project versions */
                showVersionControl
                ? { onClick: handleManageProjectVersions, content: <><Icon name={ 'edit' } color={ 'purple' }/> Manage Versions</> } : null,

                /* Roll up versions */
                showRollUpVersions
                ? { onClick: handleRollUpVersions, content: <><Icon name={ 'sync alternate' } color={ 'purple' }/> Roll Up Versions</> } : null,

            ] )
        } : null,
        hasDataSection ? {
            header: 'Data',
            items:  _.compact( [

                /* Export project / show export dialog */
                showExport
                ? { onClick: handleExportProject, content: <><Icon name={ 'file' } color={ 'purple' }/> Export Project</> } : null,

                /* Manage template project / show templating dialog */
                showTemplateManagement
                ? { onClick: handleManageProjectTemplates, content: <><Icon name={ 'file code outline' } color={ 'purple' }/> Template Management</> } : null,

                /* Template project / show templating dialog */
                showTemplateCurrentProject
                ? { onClick: handleTemplateCurrentProject, content: <><Icon name={ 'copy' } color={ 'purple' }/> Save Project as Template</> } : null,

                /* New project from template dialog */
                showCreateProject
                ? { onClick: handleCreateNewProject, content: <><Icon name={ 'magic' } color={ 'purple' }/> New Project...</> } : null,

                /* Survey builder */
                showSurveyBuilder
                ? { onClick: handleOpenSurveyBuilder, content: <><Icon name={ 'question circle outline' } color={ 'purple' }/> Survey Builder</> } : null,

            ] )
        } : null,
        showImportProjectData ? {
            header: 'Import',
            items:  [

                { onClick: handleImportData, content: <><Icon name="upload" color="purple" /> Import Data</> },
                { onClick: handleImportSmartsheet, content: <><Icon name="upload" color="purple" /> Import Smartsheet</> },
                { onClick: handleImportCustomSmartsheet, content: <><Icon name="upload" color="purple" /> Import Smartsheet (Custom Format)</> },


            ]
        } : null,
        showImportProjectData ? {
            header: 'Export',
            items: [

                { onClick: handleExportSmartsheet, content: <><Icon name="download" color="purple" /> Export Smartsheet</> },


            ]
        } : null,
        hasUserSection ? {
            header: 'User',
            items:  _.compact( [

                /* Link to dashboard / all projects */
                showSwitchProjects
                ? { onClick: handleGotoProjectSelectionPage, content: <><Icon name={ 'home' } color={ 'purple' }/> Dashboard</> } : null,

                /* Link to log out */
                showLogout
                ? { onClick: handleGotoLogoutPage, content: <><Icon name={ 'sign-out' } color={ 'purple' }/> Logout</> } : null,
            ] )
        } : null,
    ] );

    return (
        useDropdown
        ? <ProjectOperationsDropdownMenu
            menuHash={ menuHash }
            direction={ direction }
            label={ label }
            containerProps={ containerProps }
            className={ className }
        />
        : <ProjectOperationsGenericMenu
            menuHash={ menuHash }
            containerProps={ containerProps }
        />
    );
};

const OperationsMenuEmptyMessage: FC = ( props ) => {

    return (
        <StyledContainer padding={ Size.SM }>
            <FinePrintMessage><strong>Access Restricted</strong><br/>These features are offered to specific user roles - talk with your admin to learn more about access.</FinePrintMessage>
        </StyledContainer>
    );
};

const ProjectOperationsDropdownMenu: FC<{
    menuHash: MenuHash,
    direction: Props['direction'],
    label: Props['label'],
    className: Props['className'],
    containerProps: DropdownMenuProps,
}> = ( props ) => {

    let { menuHash, direction, label, containerProps, className } = props;

    return (

        <Dropdown direction={ direction }
                  icon={ null }
                  closeOnEscape={ true }
                  closeOnBlur={ true }
                  openOnFocus={ true }
                  closeOnChange={ true }
                  trigger={
                      <Flexbox className={ 'text--colored-purple text--strong ' } align={ FlexAlign.CENTER }>
                          <FlexItem>{ label }</FlexItem>
                          <FlexItem className={ 'margin-sm-left' }>
                              <Icon name={ 'sidebar' } color={ 'purple' }/>
                          </FlexItem>
                      </Flexbox>
                  }
                  className={ classNames( { 'padding-xs-vertical padding-md-horizontal container--rounded': !className }, className ) }
                  { ...containerProps }>

            <UseIf x={ _.isEmpty( menuHash ) } eq={ false } else={ () => {

                return <OperationsMenuEmptyMessage/>;

            } }>

                <Dropdown.Menu style={ direction === 'right' ? { left: 'auto', right: 0 } : {} }>


                    { menuHash.map( ( menuGroup, groupIndex ) => (
                        <React.Fragment key={ `menu-group-${ menuGroup.header }` }>
                            <>
                                <Dropdown.Header>{ menuGroup.header }</Dropdown.Header>

                                { menuGroup.items.map( ( item, index ) => (

                                    <Dropdown.Item key={ `menu-item-${ menuGroup.header }-${ index }` } onClick={ item.onClick } content={ item.content }/>

                                ) ) }
                            </>
                        </React.Fragment>
                    ) ) }


                </Dropdown.Menu>

            </UseIf>

        </Dropdown>
    );
};

const ProjectOperationsGenericMenu: FC<{
    menuHash: MenuHash,
    containerProps: MenuProps,
}> = ( props ) => {

    let { menuHash, containerProps } = props;

    return (

        <UseIf x={ _.isEmpty( menuHash ) } eq={ false } else={ () => {

            return <OperationsMenuEmptyMessage/>;

        } }>

            <Menu vertical { ...containerProps }>

                { menuHash.map( ( menuGroup ) => {

                    return (
                        <Menu.Item key={ `menu-group-${ menuGroup.header }` }>

                            <Menu.Header>{ menuGroup.header }</Menu.Header>

                            { menuGroup.items.map( ( item, index ) => (

                                <Menu.Item className={ 'no-padding-bottom no-border' } key={ `menu-item-${ menuGroup.header }-${ index }` } onClick={ item.onClick } content={ item.content }/>

                            ) ) }

                        </Menu.Item>
                    );

                } ) }

            </Menu>

        </UseIf>
    );
};

ProjectOperationsMenu.defaultProps = defaultProps;

export { ProjectOperationsMenu };
